/**
 * useSignatureCanvas Hook
 *
 * A reusable React hook for canvas-based signature capture.
 * Encapsulates all canvas initialization, drawing logic, and state management.
 *
 * Features:
 * - Automatic canvas initialization with DPI scaling
 * - Touch and mouse event handling
 * - Configurable validation (minimum strokes, size limits)
 * - Retry mechanism for failed initialization
 * - Clean state management
 *
 * @example
 * ```tsx
 * const signature = useSignatureCanvas({
 *   minStrokes: 5,
 *   maxSizeKB: 500,
 *   autoInit: true
 * });
 *
 * return (
 *   <canvas
 *     ref={signature.canvasRef}
 *     onMouseDown={signature.startDrawing}
 *     onMouseMove={signature.draw}
 *     // ... other events
 *   />
 * );
 * ```
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import {
  initializeSignatureCanvas,
  getCanvasCoordinates,
  clearSignatureCanvas,
  getSignatureDataUrl,
  type CanvasContext
} from '@/utils/canvasSignature';

export interface SignatureValidation {
  minStrokes?: number;
  maxSizeKB?: number;
  minWidth?: number;
  minHeight?: number;
}

export interface SignatureOptions {
  width?: number;
  height?: number;
  validation?: SignatureValidation;
  autoInit?: boolean;
  onInitSuccess?: () => void;
  onInitError?: (error: Error) => void;
  onSignatureChange?: (hasSignature: boolean, strokeCount: number) => void;
}

export interface UseSignatureCanvasReturn {
  // Canvas reference
  canvasRef: React.RefObject<HTMLCanvasElement>;

  // State
  isReady: boolean;
  isDrawing: boolean;
  hasSignature: boolean;
  strokeCount: number;
  error: string | null;

  // Drawing methods
  startDrawing: (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => void;
  draw: (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => void;
  stopDrawing: () => void;

  // Control methods
  clear: () => void;
  retry: () => void;
  initialize: () => void;

  // Export methods
  getDataUrl: () => string | null;
  isValid: () => boolean;
  getValidationErrors: () => string[];
}

const DEFAULT_OPTIONS: Required<SignatureOptions> = {
  width: 600,
  height: 200,
  validation: {
    minStrokes: 5,
    maxSizeKB: 500,
    minWidth: 100,
    minHeight: 50
  },
  autoInit: true,
  onInitSuccess: () => {},
  onInitError: () => {},
  onSignatureChange: () => {}
};

export function useSignatureCanvas(options: SignatureOptions = {}): UseSignatureCanvasReturn {
  const mergedOptions = { ...DEFAULT_OPTIONS, ...options };
  const { width, height, validation, autoInit, onInitSuccess, onInitError, onSignatureChange } = mergedOptions;

  // Refs
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const canvasContextRef = useRef<CanvasContext | null>(null);
  const initAttemptedRef = useRef(false);

  // State
  const [isReady, setIsReady] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [strokeCount, setStrokeCount] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // Initialize canvas
  const initialize = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || isReady) {
return;
}

    try {
      // Check if canvas is visible
      if (canvas.offsetWidth === 0 || canvas.offsetHeight === 0) {
        throw new Error('Canvas has no dimensions. Ensure it is visible.');
      }

      const displayWidth = canvas.offsetWidth || width;
      const displayHeight = height;

      const context = initializeSignatureCanvas(canvas, displayWidth, displayHeight);
      canvasContextRef.current = context;

      setIsReady(true);
      setError(null);
      initAttemptedRef.current = true;

      onInitSuccess();
    } catch (err) {
      const error = err as Error;
      setError(error.message);
      setIsReady(false);
      onInitError(error);
    }
  }, [width, height, isReady, onInitSuccess, onInitError]);

  // Auto-initialize on mount if enabled
  useEffect(() => {
    if (autoInit && !initAttemptedRef.current) {
      // Delay to ensure canvas is mounted and visible
      const timer = setTimeout(initialize, 100);
      return () => clearTimeout(timer);
    }
  }, [autoInit, initialize]);

  // Drawing event handlers
  const startDrawing = useCallback((e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    const context = canvasContextRef.current;
    if (!canvas || !context || !isReady) {
return;
}

    // Prevent touch scrolling
    if ('touches' in e) {
      e.preventDefault();
    }

    setIsDrawing(true);

    const { x, y } = getCanvasCoordinates(e, canvas);
    context.ctx.beginPath();
    context.ctx.moveTo(x, y);
  }, [isReady]);

  const draw = useCallback((e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDrawing) {
return;
}

    const canvas = canvasRef.current;
    const context = canvasContextRef.current;
    if (!canvas || !context) {
return;
}

    // Prevent touch scrolling
    if ('touches' in e) {
      e.preventDefault();
    }

    const { x, y } = getCanvasCoordinates(e, canvas);
    context.ctx.lineTo(x, y);
    context.ctx.stroke();

    // Update stroke count and signature state
    setStrokeCount(prev => {
      const newCount = prev + 1;
      const minStrokes = validation?.minStrokes ?? DEFAULT_OPTIONS.validation.minStrokes;

      if (newCount >= minStrokes && !hasSignature) {
        setHasSignature(true);
        onSignatureChange(true, newCount);
      } else {
        onSignatureChange(false, newCount);
      }

      return newCount;
    });
  }, [isDrawing, hasSignature, validation?.minStrokes, onSignatureChange]);

  const stopDrawing = useCallback(() => {
    setIsDrawing(false);
  }, []);

  // Control methods
  const clear = useCallback(() => {
    const canvas = canvasRef.current;
    const context = canvasContextRef.current;
    if (!canvas || !context) {
return;
}

    clearSignatureCanvas(context.ctx, canvas);
    setHasSignature(false);
    setStrokeCount(0);
    onSignatureChange(false, 0);
  }, [onSignatureChange]);

  const retry = useCallback(() => {
    setError(null);
    setIsReady(false);
    initAttemptedRef.current = false;

    // Re-initialize after a brief delay
    setTimeout(initialize, 100);
  }, [initialize]);

  // Export methods
  const getDataUrl = useCallback((): string | null => {
    const canvas = canvasRef.current;
    if (!canvas || !hasSignature) {
return null;
}

    return getSignatureDataUrl(canvas);
  }, [hasSignature]);

  const isValid = useCallback((): boolean => {
    if (!hasSignature) {
return false;
}

    const minStrokes = validation?.minStrokes ?? DEFAULT_OPTIONS.validation.minStrokes;
    if (strokeCount < minStrokes) {
return false;
}

    const dataUrl = getDataUrl();
    if (!dataUrl) {
return false;
}

    // Check size limit if specified
    const maxSizeKB = validation?.maxSizeKB ?? DEFAULT_OPTIONS.validation.maxSizeKB;
    const sizeInBytes = (dataUrl.length * 3) / 4;
    if (sizeInBytes > maxSizeKB * 1024) {
return false;
}

    return true;
  }, [hasSignature, strokeCount, validation?.minStrokes, validation?.maxSizeKB, getDataUrl]);

  const getValidationErrors = useCallback((): string[] => {
    const errors: string[] = [];

    if (!hasSignature) {
      errors.push('No signature drawn');
    }

    const minStrokes = validation?.minStrokes ?? DEFAULT_OPTIONS.validation.minStrokes;
    if (strokeCount < minStrokes) {
      errors.push(`Minimum ${minStrokes} strokes required (current: ${strokeCount})`);
    }

    const dataUrl = getDataUrl();
    if (dataUrl) {
      const maxSizeKB = validation?.maxSizeKB ?? DEFAULT_OPTIONS.validation.maxSizeKB;
      const sizeInBytes = (dataUrl.length * 3) / 4;
      const sizeInKB = sizeInBytes / 1024;

      if (sizeInKB > maxSizeKB) {
        errors.push(`Signature too large (${Math.round(sizeInKB)}KB, max: ${maxSizeKB}KB)`);
      }
    }

    return errors;
  }, [hasSignature, strokeCount, validation?.minStrokes, validation?.maxSizeKB, getDataUrl]);

  return {
    // Canvas reference
    canvasRef,

    // State
    isReady,
    isDrawing,
    hasSignature,
    strokeCount,
    error,

    // Drawing methods
    startDrawing,
    draw,
    stopDrawing,

    // Control methods
    clear,
    retry,
    initialize,

    // Export methods
    getDataUrl,
    isValid,
    getValidationErrors
  };
}